import * as React from 'react';
import Input from '@mui/material/Input';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl, { FormControlProps } from '@mui/material/FormControl';
import Select, { SelectProps } from '@mui/material/Select';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MUICircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import { sortAlphaNum } from 'helpers/helpers';
import { SxProps } from '@mui/system';

const CircularProgress = () => (
    <MUICircularProgress
        size="20px"
        style={{
            display: 'flex',
            marginRight: '12px',
        }}
    />
);

type OptionT<T> = { value: T; label: string };
type Props<T> = {
    preventSorting?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    onChange: (e: T | T[]) => void;
    options: OptionT<T>[];
    className?: string;
    label?: string;
    value: T | T[];
    selectorId?: string;
    multiple?: boolean;
    classes?: object;
    variant?: SelectProps['variant'];
    loading?: boolean;
    sx?: SxProps;
    error?: FormControlProps['error'];
};

const Selector = <T,>({
    preventSorting = false,
    disabled = false,
    fullWidth = true,
    onChange,
    options,
    className = undefined,
    label = undefined,
    value,
    selectorId = undefined,
    multiple = false,
    classes = {},
    variant = 'standard',
    loading = false,
    error = false,
    sx = {},
}: Props<T>) => {
    const { t } = useTranslation();
    const sortedOptions = preventSorting
        ? options
        : options.sort((a, b) => sortAlphaNum(a.label, b.label));
    const handleChange = (
        e: React.ChangeEvent<{
            name?: string;
            value: T;
        }>,
    ) => {
        onChange(e.target.value);
        e.stopPropagation();
    };
    const IconComponent = loading ? CircularProgress : KeyboardArrowDownIcon;
    return (
        <FormControl
            fullWidth={fullWidth}
            className={className}
            variant={variant}
            margin={variant === 'outlined' ? 'dense' : 'none'}
            size={variant === 'outlined' ? 'small' : 'medium'}
            disabled={disabled || loading}
            sx={sx}
            error={error}
        >
            {label && (
                <InputLabel htmlFor={selectorId} variant={variant}>
                    {t(label)}
                </InputLabel>
            )}
            <Select
                variant={variant}
                data-testid={className}
                value={value}
                /* We should not pass T but only strings, because objects are invalid HTML values for <select /> */
                // @ts-expect-error MUI migration
                onChange={handleChange}
                input={
                    variant === 'outlined' ? (
                        <OutlinedInput
                            id={selectorId}
                            label={label ? t(label) : undefined}
                        />
                    ) : (
                        <Input id={selectorId} />
                    )
                }
                label={label}
                IconComponent={IconComponent}
                multiple={multiple}
                classes={classes}
            >
                {sortedOptions.length > 0 ? (
                    sortedOptions.map((option) => (
                        // @ts-expect-error [TS migration] Was not detected by flow
                        <MenuItem
                            value={option.value}
                            data-e2e-value={option.value}
                            key={option.label}
                        >
                            {t(option.label)}
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem value="" key="noData">
                        {t('common.noData')}
                    </MenuItem>
                )}
            </Select>
        </FormControl>
    );
};

export default React.memo(Selector);
